import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { StudentPointsBoard } from '../admin/points/AdminPointsPage';
import { clearTeacherSession, readTeacherSession, saveTeacherSession, teacherRequest } from './teacherModeApi';
import TeacherPinDialog from './components/TeacherPinDialog';
import TeacherLibraryBoard from './components/TeacherLibraryBoard';
import TeacherLibraryApprovals, { TeacherLibraryApprovalCard } from './components/TeacherLibraryApprovals';
import LibraryPointAnimations from '../admin/library/components/LibraryPointAnimations';
import { fetchJson } from '../apiBase';
import TeacherStarQuickBoard from './components/TeacherStarQuickBoard';
import BoardHeader from './components/BoardHeader';
import TeacherAttendanceBoard from './components/TeacherAttendanceBoard';
import TeacherMaterialBoard from './components/TeacherMaterialBoard';
import LearnedLettersDialog from './wheel/LearnedLettersDialog';
import TeacherHomeworkFlow from './homework/TeacherHomeworkFlow';
import TeacherHomeBonus from './components/TeacherHomeBonus';
import TeacherCeteles from './components/TeacherCeteles';
import useDanduClassLive from '../live/useDanduClassLive';

const tools = [
  { key: 'ceteles', title: 'Çetelelerim', description: 'Sınıfının çetelelerini aç, değerlendirme ekle ve sonuçları kaydet.', icon: <FactCheckRoundedIcon sx={{ fontSize: { xs: 34, md: 58 }, color: '#6941c6' }} />, color: '#f3edff', arrowColor: '#e9ddff', arrowInk: '#6941c6' },
  { key: 'homeworks', title: 'Ödev Değerlendir', description: 'Ödev listesini aç, öğrencilerin ödev durumlarını değerlendir.', icon: <FactCheckRoundedIcon sx={{ fontSize: { xs: 34, md: 58 }, color: '#2563eb' }} />, color: '#eaf3ff', arrowColor: '#dbeafe', arrowInk: '#2563eb' },
  { key: 'wheel', title: 'Öğrenilen Harfler', description: 'Çark ve Balon Patlat için sınıfın öğrendiği harfleri kaydet.', icon: <MenuBookRoundedIcon sx={{ fontSize: { xs: 34, md: 58 }, color: '#7936ee' }} />, color: '#faf0ff', arrowColor: '#ead7ff', arrowInk: '#7936ee' },
  { key: 'materials', title: 'Öğrenci Materyalleri', description: 'Materyal durumlarını Tam veya Eksik olarak değerlendir.', icon: <FactCheckRoundedIcon sx={{ fontSize: { xs: 34, md: 58 }, color: '#b7791f' }} />, color: '#fff7e7', arrowColor: '#ffebc3', arrowInk: '#9a6500' },
  { key: 'attendance', title: 'Yoklama Al', description: 'Bugünün yoklamasını al, kayıtsız öğrencileri hızlıca işaretle.', icon: <FactCheckRoundedIcon sx={{ fontSize: { xs: 34, sm: 42, md: 58 }, color: '#16a06a' }} />, color: '#e8faf1', arrowColor: '#d1f3e1', arrowInk: '#168659' },
  { key: 'points', title: 'Öğrenci Puanları', description: 'Öğrencilerin günlük puanlarını ve puan geçmişini görüntüle.', icon: <PaidRoundedIcon sx={{ fontSize: { xs: 34, sm: 42, md: 58 }, color: '#7c3aed' }} />, color: '#f3e8ff', arrowColor: '#e9d5ff', arrowInk: '#7c3aed' },
  { key: 'stars', title: 'Yıldız Bahçem', description: 'Öğrencilere davranış yıldızı ver ve yıldızları yönet.', icon: <StarRoundedIcon sx={{ fontSize: { xs: 34, sm: 42, md: 58 }, color: '#ffb900', filter: 'drop-shadow(0 3px 1px rgba(153,94,0,.18))' }} />, color: '#fff8df', arrowColor: '#ffe4a4', arrowInk: '#f59e0b' },
  { key: 'library', title: 'Kütüphanem', description: 'Kitapları yönet ve okuma kayıtlarını görüntüle.', icon: <MenuBookRoundedIcon sx={{ fontSize: { xs: 32, sm: 40, md: 54 }, color: '#2563eb', filter: 'drop-shadow(0 3px 1px rgba(37,99,235,.12))' }} />, color: '#eaf3ff', arrowColor: '#dbeafe', arrowInk: '#2563eb' },
];

export default function TeacherModePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readTeacherSession(token));
  const [pin, setPin] = useState('');
  const [section, setSection] = useState(() => {
    try { return readTeacherSession(token) && window.sessionStorage.getItem(`smartboard_teacher_view:${token}`) === 'attendance' ? 'attendance' : ''; }
    catch (_) { return ''; }
  });
  const [libraryTab, setLibraryTab] = useState('overview');
  const [showApprovals, setShowApprovals] = useState(false);
  const [data, setData] = useState(null);
  const [classCode, setClassCode] = useState('');
  const [classId, setClassId] = useState(null);
  const [gradeLevel, setGradeLevel] = useState(null);
  const [studentId, setStudentId] = useState('');
  const [bookId, setBookId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attendancePending, setAttendancePending] = useState(false);
  const [cetelesPending, setCetelesPending] = useState(false);
  const attendanceRef = useRef(null);
  const materialsRef = useRef(null);
  const [materialsPending, setMaterialsPending] = useState(false);
  const lastServerActivity = useRef(Date.now());
  const classLiveConnected = useDanduClassLive({
    enabled: Boolean(session && section === 'library' && data && !showApprovals),
    path: token ? `/mini-tasks/board/${encodeURIComponent(token)}/teacher/live/events` : null,
    sessionHeader: session,
    onUnauthorized: () => {
      // The PIN session does not gain activity from an idle SSE stream.
      clearTeacherSession(token);
      setSession(null);
      setData(null);
      setSection('');
      setError('Öğretmen Modu oturumunun süresi doldu. PIN ile tekrar giriş yapın.');
    },
  });

  useEffect(() => {
    try {
      if (session && section === 'attendance') window.sessionStorage.setItem(`smartboard_teacher_view:${token}`, 'attendance');
      else window.sessionStorage.removeItem(`smartboard_teacher_view:${token}`);
    } catch (_) { /* Session storage may be unavailable. */ }
  }, [section, session, token]);

  const expire = useCallback((message) => {
    clearTeacherSession(token);
    setSession(null);
    setData(null);
    setPin('');
    setClassId(null);
    setAttendancePending(false);
    setMaterialsPending(false);
    setSection((current) => current === 'attendance' || current === 'materials' ? '' : current);
    setError(message || 'Öğretmen Modu süresi doldu. Lütfen PIN ile tekrar giriş yapın.');
  }, [token]);

  const request = useCallback(async (path, options) => {
    const credential = readTeacherSession(token);
    try {
      const result = await teacherRequest(token, path, credential, options);
      lastServerActivity.current = Date.now();
      return result;
    } catch (err) {
      if (err.status === 401 && credential && readTeacherSession(token) === credential) expire(err.message);
      throw err;
    }
  }, [expire, token]);

  useEffect(() => {
    if (!session) return undefined;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => expire(), 30 * 60 * 1000);
      // Only user activity keeps an actively edited note session alive.
      if (Date.now() - lastServerActivity.current > 5 * 60 * 1000) {
        lastServerActivity.current = Date.now();
        request('session').catch(() => {});
      }
    };
    reset();
    window.addEventListener('pointerdown', reset);
    window.addEventListener('keydown', reset);
    return () => { clearTimeout(timer); window.removeEventListener('pointerdown', reset); window.removeEventListener('keydown', reset); };
  }, [session, request, expire]);

  useEffect(() => {
    if (!session) return;
    let active = true;
    request('session').then((payload) => {
      if (active) {
        setClassCode(payload.class?.code || '');
        setClassId(payload.class?.id ?? null);
        setGradeLevel(payload.class?.grade_level ?? null);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, [request, session]);

  const load = useCallback(async (key) => {
    if (key === 'points' || key === 'attendance' || key === 'materials' || key === 'wheel' || key === 'homeworks' || key === 'ceteles') {
      setData(null);
      setError('');
      setSection(key);
      return;
    }
    setBusy(true);
    setError('');
    try {
      setData(await request(key));
      if (key === 'library') { setLibraryTab('overview'); setShowApprovals(false); }
      setSection(key);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [request]);

  const loadPoints = useCallback(() => request('points'), [request]);
  const pointAwardApi = useMemo(() => ({
    storageKey: `teacher_point_award:board:${token}`,
    submit: (body) => request('point-awards', { method: 'POST', body: JSON.stringify(body) }),
  }), [request, token]);
  const refreshLibraryAfterAward = useCallback(() => {
    // An automatic +5 is NOT an activity performed by the teacher.
    // Refresh via the read-only route without renewing PIN last_active_at.
    const current = readTeacherSession(token);
    if (!current) return;
    teacherRequest(token, 'library/live-refresh', current)
      .then(setData)
      .catch((err) => {
        if (err?.status === 401) expire(err.message);
      });
  }, [expire, token]);

  const loadLibraryEvents = useCallback(async (after) => {
    const suffix = after === null ? '' : `?after=${after}`;
    const res = await fetchJson(`/mini-tasks/board/${encodeURIComponent(token)}/library/point-events${suffix}`);
    if (!res.ok) throw new Error('Puan etkinlikleri yüklenemedi.');
    return res.json();
  }, [token]);

  const loadPointHistory = useCallback((id, limit, offset) => request(`points/${encodeURIComponent(id)}?limit=${limit}&offset=${offset}`), [request]);

  useEffect(() => {
    if (session && section) load(section);
    // Refresh section after a new login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const login = async (event) => {
    event.preventDefault();
    if (!/^[0-9]{4}$/.test(pin)) return;
    setBusy(true);
    setError('');
    try {
      const result = await teacherRequest(token, 'login', null, { method: 'POST', body: JSON.stringify({ pin }) });
      saveTeacherSession(token, result.session_token);
      setClassCode(result.class?.code || '');
      setClassId(result.class?.id ?? null);
      setSession(result.session_token);
      setPin('');
    } catch (err) {
      setError(err.message);
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    setBusy(true);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try { await request('logout', { method: 'POST', signal: controller.signal }); } catch (_) { /* Clear the local session regardless. */ }
    finally { clearTimeout(timer); }
    clearTeacherSession(token);
    setSession(null);
    setBusy(false);
    navigate(`/mini-tasks/${token}`);
  };

  const mutate = async (path, options) => {
    setBusy(true);
    setError('');
    try {
      await request(path, options);
      setData(await request(section));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const students = data?.students || [];
  const selected = students.find((student) => String(student.student_id ?? student.id) === String(studentId));
  const completed = data?.completions?.some((item) => String(item.student_id) === String(studentId) && String(item.book_id) === String(bookId));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fffaf0', color: '#172554', pb: 4, backgroundImage: 'radial-gradient(circle at 0% 100%, rgba(134,204,22,.12), transparent 15%), radial-gradient(circle at 100% 100%, rgba(250,204,21,.12), transparent 18%)' }}>
      <Box sx={{ mx: 'auto', maxWidth: 1720, px: { xs: 1.2, sm: 2, md: 3.2 } }}>
        <BoardHeader
          classTitle={classCode ? `${classCode} Sınıfı` : 'Sınıf'}
          mode="teacher"
          onLogout={() => section === 'attendance' && attendanceRef.current
             ? attendanceRef.current.requestLeave(logout)
             : section === 'materials' && materialsRef.current ? materialsRef.current.requestLeave(logout) : logout()}
          logoutDisabled={!session || busy || attendancePending || materialsPending || cetelesPending || section === 'wheel'}
        />
      </Box>

      <Box sx={{ maxWidth: section === 'homeworks' ? 'none' : section === 'stars' || section === 'attendance' || section === 'materials' || section === 'ceteles' || showApprovals ? 1720 : section === 'wheel' ? 1440 : 1160, mx: 'auto', px: section === 'homeworks' ? 0 : { xs: 1.5, md: 4 }, pt: section === 'homeworks' ? 0 : section === 'points' || section === 'attendance' || section === 'materials' || section === 'ceteles' || section === 'wheel' ? { xs: 1.5, md: 2 } : { xs: 2, md: 5 } }}>
        {!section && <>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'flex-start' }, gap: 2, mb: { xs: 2, md: 4 } }}>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', md: 'left' } }}>
            <Typography sx={{ fontSize: { xs: 18, sm: 32, md: 39 }, lineHeight: 1.15, fontWeight: 1000, color: '#1e275a' }}>Merhaba Öğretmenimiz 👋</Typography>
            <Typography sx={{ mt: .7, color: '#3e4a77', fontSize: { xs: 11, sm: 14, md: 19 }, fontWeight: 600, lineHeight: 1.4 }}>
              {classCode || 'Bu'} sınıfı için öğretmen modundasınız.<br />Aşağıdaki menülerden devam edebilirsiniz.
            </Typography>
            </Box>
            {session && classId !== null && <TeacherHomeBonus key={`${token}:${session}`} loadRoster={loadPoints} api={pointAwardApi} />}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: { xs: 1, md: 2.5 }, '@media (max-width: 340px)': { gridTemplateColumns: '1fr' } }}>
            {tools.filter((item) => item.key !== 'wheel' || String(gradeLevel) === '1').map((item) => <Paper key={item.key} component="button" type="button" onClick={() => load(item.key)} elevation={0} sx={{ position: 'relative', border: '1px solid #ececf2', borderRadius: { xs: 2.5, md: 4 }, p: { xs: 1.1, sm: 2.5, md: 3.5 }, minHeight: { xs: 110, sm: 150, md: 178 }, textAlign: 'left', cursor: 'pointer', color: '#1f2b5c', bgcolor: '#fff', boxShadow: '0 5px 18px rgba(35,39,83,.08)', display: 'flex', alignItems: 'flex-start', gap: { xs: .8, sm: 2, md: 2.5 }, transition: 'transform .18s, box-shadow .18s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 26px rgba(35,39,83,.13)' }, '&:focus-visible': { outline: '3px solid #7c56e7', outlineOffset: 2 } }}>
              <Box sx={{ bgcolor: item.color, borderRadius: '50%', width: { xs: 44, sm: 70, md: 88 }, height: { xs: 44, sm: 70, md: 88 }, flex: '0 0 auto', display: 'grid', placeItems: 'center' }}>{item.icon}</Box>
              <Box sx={{ minWidth: 0, pr: 2 }}>
                <Typography sx={{ fontWeight: 1000, fontSize: { xs: 13, sm: 19, md: 25 }, lineHeight: 1.2 }}>{item.title}</Typography>
                <Typography sx={{ mt: .5, color: '#35426e', fontWeight: 600, fontSize: { xs: 9, sm: 13, md: 16 }, lineHeight: 1.3 }}>{item.description}</Typography>
              </Box>
              <Box sx={{ position: 'absolute', right: { xs: 9, md: 18 }, bottom: { xs: 8, md: 15 }, width: { xs: 24, md: 34 }, height: { xs: 24, md: 34 }, borderRadius: '50%', bgcolor: item.arrowColor, color: item.arrowInk, display: 'grid', placeItems: 'center' }}><ArrowForwardRoundedIcon sx={{ fontSize: { xs: 17, md: 21 } }} /></Box>
            </Paper>)}
          </Box>
          <Box sx={{ mt: { xs: 2, md: 3 }, p: { xs: 1, md: 2 }, borderRadius: 3, bgcolor: '#f0f0ff', display: 'flex', alignItems: 'flex-start', gap: 1, color: '#354378' }}>
            <InfoRoundedIcon sx={{ color: '#6636e0', fontSize: { xs: 18, md: 29 }, flex: '0 0 auto' }} />
            <Box>
              <Typography sx={{ fontSize: { xs: 10, md: 16 }, fontWeight: 900 }}>Bu mod sadece {classCode || 'bu'} sınıfı için geçerlidir.</Typography>
              <Typography sx={{ fontSize: { xs: 9, md: 14 }, lineHeight: 1.4 }}>Tarayıcı kapatıldığında veya 30 dakika işlem yapılmadığında oturum otomatik olarak sonlanır.</Typography>
            </Box>
          </Box>
        </>}

        {section && <>
          {section !== 'wheel' && section !== 'homeworks' && section !== 'stars' && section !== 'points' && section !== 'attendance' && section !== 'materials' && section !== 'ceteles' && !showApprovals && <Box sx={{ mb: { xs: 2.5, md: 3.5 } }}>
            <Button onClick={() => { setSection(''); setData(null); setError(''); }} startIcon={<ArrowBackRoundedIcon />} sx={{ display: 'inline-flex', px: 0, mb: { xs: 1.5, md: 2 }, fontWeight: 900, textTransform: 'none' }}>Öğretmen Moduna Dön</Button>
            {section !== 'wheel' && <Typography component="h1" sx={{ display: 'block', m: 0, fontSize: { xs: 30, md: 42 }, lineHeight: 1.15, fontWeight: 900 }}>{tools.find((item) => item.key === section)?.title}</Typography>}
          </Box>}
          {busy && <CircularProgress size={28} sx={{ mb: 2 }} />}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {section === 'ceteles' && session && <TeacherCeteles key={`${token}:${session}`} classCode={classCode} onRequest={request} onPendingChange={setCetelesPending}
            onBack={() => { setSection(''); setData(null); setError(''); }} />}
          {section === 'homeworks' && session && <TeacherHomeworkFlow key={`${token}:${session}`} classCode={classCode} onRequest={request}
            onBack={() => { setSection(''); setData(null); setError(''); }} />}
          {section === 'wheel' && session && <LearnedLettersDialog token={token}
            onSaved={() => { setSection(''); setData(null); setError(''); }} onCancel={() => { setSection(''); setData(null); setError(''); }} />}
          {section === 'stars' && data && <TeacherStarQuickBoard board={data} classCode={classCode} classId={classId} onBack={() => { setSection(''); setData(null); setError(''); }} onRequest={request} onBoardChange={setData} />}
          {section === 'points' && session && <StudentPointsBoard key={session} loadRoster={loadPoints} loadHistory={loadPointHistory} awardApi={pointAwardApi} classScoped onBack={() => { setSection(''); setData(null); setError(''); }} />}
          {section === 'attendance' && session && <TeacherAttendanceBoard ref={attendanceRef} classCode={classCode} onRequest={request}
            onBack={() => { setSection(''); setData(null); setError(''); }} onLogout={logout} onPendingChange={setAttendancePending} />}
          {section === 'materials' && session && <TeacherMaterialBoard ref={materialsRef} classCode={classCode} onRequest={request}
            onBack={() => { setSection(''); setData(null); setError(''); }} onLogout={logout} onPendingChange={setMaterialsPending} />}
          {section === 'library' && session && showApprovals && <TeacherLibraryApprovals request={request} classCode={classCode} onBack={() => load('library')} />}
          {section === 'library' && data && !showApprovals && <>
            <LibraryPointAnimations loadEvents={loadLibraryEvents} classLive classLiveConnected={classLiveConnected} onAwards={refreshLibraryAfterAward} />
            <Paper elevation={2} sx={{ borderRadius: 4, mb: 3, overflow: 'hidden' }}>
              <Tabs value={libraryTab} onChange={(_, value) => setLibraryTab(value)} variant="scrollable" scrollButtons="auto" aria-label="Kütüphanem bölümleri" sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 900, fontSize: { xs: 13, md: 17 }, minHeight: 60 } }}>
                <Tab value="overview" label="Kütüphanem" />
                <Tab value="mark" label="Kitap Okundu Olarak İşaretle" />
                <Tab value="records" label="Okuma Kayıtları" />
              </Tabs>
            </Paper>
            {libraryTab === 'overview' && <><TeacherLibraryApprovalCard count={(data.pending_claims || []).length} onOpen={() => setShowApprovals(true)} /><TeacherLibraryBoard students={data.card_students || []} classCode={classCode} /></>}
            {libraryTab === 'mark' && <Paper sx={{ borderRadius: 4, p: { xs: 2, md: 4 }, border: '1px solid #dce3e8' }}>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>Kitap Okundu Olarak İşaretle</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <FormControl fullWidth><InputLabel id="teacher-library-mark-student-label">Öğrenci</InputLabel><Select labelId="teacher-library-mark-student-label" label="Öğrenci" value={studentId} onChange={(e) => setStudentId(e.target.value)}>{students.map((s) => <MenuItem key={s.student_id ?? s.id} value={String(s.student_id ?? s.id)}>{s.name || `${s.first_name || ''} ${s.last_name || ''}`}</MenuItem>)}</Select></FormControl>
                <FormControl fullWidth><InputLabel id="teacher-library-mark-book-label">Kitap</InputLabel><Select labelId="teacher-library-mark-book-label" label="Kitap" value={bookId} onChange={(e) => setBookId(e.target.value)}>{(data.books || []).map((b) => <MenuItem key={b.id} value={String(b.id)}>{b.title}</MenuItem>)}</Select></FormControl>
              </Stack>
              {selected && <Typography sx={{ mb: 2, color: '#52637a' }}>Okunan kitap: {(data.completions || []).filter((c) => String(c.student_id) === String(studentId)).length}</Typography>}
              <Button disabled={!studentId || !bookId || busy} variant={completed ? 'outlined' : 'contained'} onClick={() => mutate('library', { method: 'POST', body: JSON.stringify({ student_id: Number(studentId), book_id: Number(bookId), completed: !completed }) })}>{completed ? 'Okundu İşaretini Kaldır' : 'Okundu Olarak İşaretle'}</Button>
            </Paper>}
            {libraryTab === 'records' && <Paper sx={{ borderRadius: 4, p: { xs: 2, md: 4 }, border: '1px solid #dce3e8' }}>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>Okuma Kayıtları</Typography>
              <FormControl fullWidth sx={{ maxWidth: 420, mb: 3 }}><InputLabel id="teacher-library-record-student-label">Öğrenci</InputLabel><Select labelId="teacher-library-record-student-label" label="Öğrenci" value={studentId} onChange={(e) => setStudentId(e.target.value)}>{students.map((s) => <MenuItem key={s.student_id ?? s.id} value={String(s.student_id ?? s.id)}>{s.name || `${s.first_name || ''} ${s.last_name || ''}`}</MenuItem>)}</Select></FormControl>
              {!studentId && <Typography sx={{ color: '#64748b' }}>Kayıtları görmek için bir öğrenci seçin.</Typography>}
              {studentId && (data.completions || []).filter((entry) => String(entry.student_id) === String(studentId)).length === 0 && <Typography sx={{ color: '#64748b' }}>Henüz okuma kaydı yok.</Typography>}
              <Stack spacing={1}>
                {(data.completions || []).filter((entry) => String(entry.student_id) === String(studentId)).map((entry) => <Box key={entry.book_id} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f5f8fb', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontWeight: 700 }}>{data.books.find((book) => book.id === entry.book_id)?.title || 'Kitap'}</Typography>
                  <Typography sx={{ color: '#64748b' }}>{entry.finished_date || ''}</Typography>
                </Box>)}
              </Stack>
            </Paper>}
          </>}
        </>}
      </Box>

      <TeacherPinDialog
        open={!session}
        pin={pin}
        onPinChange={setPin}
        onClose={() => { setPin(''); setError(''); navigate(`/mini-tasks/${token}`); }}
        onSubmit={login}
        busy={busy}
        error={error}
      />
    </Box>
  );
}
